import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { markdown, markdownKeymap, markdownLanguage } from "@codemirror/lang-markdown";
import { bracketMatching } from "@codemirror/language";
import { languages } from "@codemirror/language-data";
import { Annotation, EditorState, type Extension } from "@codemirror/state";
import { drawSelection, EditorView, keymap, placeholder } from "@codemirror/view";
import type { Autosave, ContentDraft } from "@hq/client";
import { useAutosave, useNote } from "@hq/client-react";
import { deriveTitle } from "@hq/core";
import {
  Button,
  cn,
  EmptyState,
  Kbd,
  Skeleton,
  useShortcut,
  eventMatches,
  detectPlatform,
} from "@z/ui";
import { type Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { AddTodoPopover } from "./AddTodo";
import {
  addTodo,
  addTodoHandler,
  type AddTodoRequest,
  editorKeymap,
  replaceDocSpec,
} from "./commands";
import { editorHighlighting, editorTheme } from "./theme";

export interface NoteEditorHandle {
  focus(): void;
  getDoc(): string;
  /**
   * Replaces the selection with `text` and focuses the editor. Called before the note has
   * loaded, the text is queued and inserted as soon as the view mounts (with the cursor after
   * it), so a caller can hand over text typed while the note was being created.
   */
  insertText(text: string): void;
  /** Text of the line holding the cursor; empty before the note has loaded. */
  getLine(): string;
  /** Previews `text` at the caret without touching the document; `""` clears it. */
  setGhostText(text: string): void;
  /** Opens the "Add as todo" popover for the selection or the cursor's line. */
  openAddTodo(): void;
}

export interface NoteEditorProps {
  noteId: string;
  onOpenNote?: ((noteId: string) => void) | undefined;
  /** Focus the editor with the cursor at the end of the document once it mounts. */
  autoFocus?: boolean;
  /** Called when the title derived from the document (first heading or line) changes. */
  onTitleChange?: (title: string) => void;
  className?: string;
  ref?: Ref<NoteEditorHandle>;
}

/** Marks transactions that come from the store (sync pull, another pane), not from the user. */
const externalChange = Annotation.define<boolean>();

type Callbacks = {
  addTodoShortcut?: string | undefined;
  onTitleChange: ((title: string) => void) | undefined;
  onAddTodo: ((request: AddTodoRequest) => void) | undefined;
  onSelectionChange: ((hasSelection: boolean) => void) | undefined;
};

function createExtensions(
  noteId: string,
  autosave: Autosave<ContentDraft>,
  callbacks: { current: Callbacks },
  revision: { current: number | undefined },
): Extension {
  let lastTitle: string | undefined;
  let lastHasSelection = false;
  return [
    history(),
    drawSelection(),
    bracketMatching(),
    markdown({ base: markdownLanguage, codeLanguages: languages, addKeymap: false }),
    editorHighlighting,
    // Order is precedence: the editor's own bindings first (Mod-Enter shadows insertBlankLine),
    // then markdown's list/quote continuation, then the defaults. Tab is unbound on purpose
    // (no `indentWithTab`) so keyboard users can move focus out of the editor.
    keymap.of([...editorKeymap, ...markdownKeymap, ...defaultKeymap, ...historyKeymap]),
    EditorView.lineWrapping,
    placeholder("Start writing…"),
    editorTheme,
    EditorView.contentAttributes.of({ "aria-label": "Note", spellcheck: "true" }),
    addTodoHandler.of((request) => callbacks.current.onAddTodo?.(request)),
    EditorView.updateListener.of((update) => {
      if (update.selectionSet || update.docChanged) {
        const hasSelection = !update.state.selection.main.empty;
        if (hasSelection !== lastHasSelection) {
          lastHasSelection = hasSelection;
          callbacks.current.onSelectionChange?.(hasSelection);
        }
      }
      if (!update.docChanged) return;
      if (update.transactions.some((tr) => tr.annotation(externalChange))) return;
      const doc = update.state.doc.toString();
      autosave.update(noteId, doc, revision.current);
      const onTitleChange = callbacks.current.onTitleChange;
      if (onTitleChange) {
        const title = deriveTitle(doc);
        if (title !== lastTitle) {
          lastTitle = title;
          onTitleChange(title);
        }
      }
    }),
    EditorView.domEventHandlers({
      keydown: (event, view) => {
        const shortcut = callbacks.current.addTodoShortcut;
        if (
          shortcut &&
          !event.isComposing &&
          !event.repeat &&
          eventMatches(event, shortcut, detectPlatform())
        ) {
          event.preventDefault();
          return addTodo(view);
        }
        return false;
      },
      blur: () => {
        void autosave.flush(noteId);
        return false;
      },
    }),
  ];
}

/**
 * One CodeMirror `EditorView` per `noteId`, mounted imperatively. Keystrokes go straight to
 * `autosave.update`; nothing round-trips through React state. Store changes that arrive while
 * the editor is clean (`idle`/`saved`) replace the document with the selection mapped through;
 * while it is `dirty`, `saving` or in `error` the editor wins and the store catches up on save.
 */
export function NoteEditor({
  noteId,
  autoFocus = false,
  onTitleChange,
  className,
  ref,
}: NoteEditorProps) {
  const { data: note, isLoading } = useNote(noteId);
  const addTodoShortcut = useShortcut("editor.addTodo");
  const autosave = useAutosave();
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const revisionRef = useRef<number | undefined>(undefined);
  const [addTodoRequest, setAddTodoRequest] = useState<AddTodoRequest | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const callbacksRef = useRef<Callbacks>({
    onTitleChange: undefined,
    onAddTodo: setAddTodoRequest,
    onSelectionChange: setHasSelection,
  });
  /** Text handed to `insertText` before the view existed; applied on mount. */
  const pendingInsertRef = useRef<string | null>(null);
  // `isLoading` with stale `data` happens for one render when `noteId` changes; never seed a
  // view for the new note with the previous note's text.
  const storeDoc = isLoading ? undefined : note?.markdown;

  useEffect(() => {
    callbacksRef.current.onTitleChange = onTitleChange;
    callbacksRef.current.addTodoShortcut = addTodoShortcut;
  }, [onTitleChange, addTodoShortcut]);

  useImperativeHandle(
    ref,
    () => ({
      focus() {
        viewRef.current?.focus();
      },
      getDoc() {
        return viewRef.current?.state.doc.toString() ?? "";
      },
      insertText(text) {
        const view = viewRef.current;
        if (!view) {
          pendingInsertRef.current = (pendingInsertRef.current ?? "") + text;
          return;
        }
        view.dispatch(view.state.replaceSelection(text), { scrollIntoView: true });
        view.focus();
      },
      getLine() {
        const view = viewRef.current;
        return view ? view.state.doc.lineAt(view.state.selection.main.head).text : "";
      },
      setGhostText() {
        // The retained source editor has no dictation surface; previews are a no-op here.
      },
      openAddTodo() {
        const view = viewRef.current;
        if (view) addTodo(view);
      },
    }),
    [],
  );

  // Lifecycle per note: the cleanup destroys the view and flushes whatever the editor had
  // pending for this note (unmount, note switch, Strict Mode's remount).
  useEffect(() => {
    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
      void autosave.flush(noteId);
    };
  }, [noteId, autosave]);

  // Create the view once the note is loaded; afterwards reconcile external document changes.
  useEffect(() => {
    const host = hostRef.current;
    const existing = viewRef.current;
    if (storeDoc === undefined || !host) {
      // The note vanished (deleted elsewhere): its host is gone, so the view goes too.
      if (existing) {
        existing.destroy();
        viewRef.current = null;
        void autosave.flush(noteId);
      }
      return;
    }
    if (existing) {
      const state = autosave.getState(noteId);
      if (state !== "idle" && state !== "saved") return;
      const spec = replaceDocSpec(existing.state, storeDoc);
      revisionRef.current = note?.revision;
      if (spec) existing.dispatch({ ...spec, annotations: externalChange.of(true) });
      return;
    }
    revisionRef.current = note?.revision;
    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: storeDoc,
        selection: { anchor: autoFocus ? storeDoc.length : 0 },
        extensions: createExtensions(noteId, autosave, callbacksRef, revisionRef),
      }),
    });
    viewRef.current = view;
    const pending = pendingInsertRef.current;
    if (pending !== null) {
      pendingInsertRef.current = null;
      view.dispatch(view.state.replaceSelection(pending), { scrollIntoView: true });
      view.focus();
    } else if (autoFocus) view.focus();
  }, [noteId, storeDoc, note?.revision, autosave, autoFocus]);

  // Flush everything when the app backgrounds or the page is torn down (research.md Section 9).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") void autosave.flushAll();
    };
    const onPageHide = () => void autosave.flushAll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [autosave]);

  if (isLoading) {
    return (
      <div
        className={cn("mx-auto flex w-full max-w-[72ch] flex-col gap-3 px-4 py-6", className)}
        data-note-editor-loading=""
      >
        <Skeleton className="h-6 w-2/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }
  if (!note) {
    return (
      <EmptyState
        className={className}
        title="Note not found"
        description="It may have been deleted on another device."
      />
    );
  }
  const closeAddTodo = () => {
    setAddTodoRequest(null);
    viewRef.current?.focus();
  };
  return (
    <div
      data-note-editor={noteId}
      className={cn("relative h-full min-h-0 w-full overflow-hidden bg-bg text-fg", className)}
    >
      <div ref={hostRef} className="h-full min-h-0 w-full" />
      {hasSelection && addTodoRequest === null && (
        <div
          role="toolbar"
          aria-label="Selection"
          data-selection-toolbar=""
          className="absolute top-2 right-3 z-sticky flex items-center gap-1 rounded-md border border-border bg-surface-raised p-0.5 shadow-sm"
        >
          <Button
            variant="ghost"
            size="sm"
            // mousedown would move focus (and the selection) out of the editor before click.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              const view = viewRef.current;
              if (view) addTodo(view);
            }}
          >
            Add as todo
            {addTodoShortcut ? <Kbd keys={addTodoShortcut} className="ml-1" /> : null}
          </Button>
        </div>
      )}
      {addTodoRequest !== null && (
        <AddTodoPopover sourceNoteId={noteId} request={addTodoRequest} onClose={closeAddTodo} />
      )}
    </div>
  );
}
